import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useSnackbar } from 'notistack'
import { FonteDefesa, iaService } from '@/services/iaService'
import { documentoService } from '@/services/documentoService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { Documento } from '@/types/documento'
import { formularioService } from '@/services/formularioService'
import { ItemContextoIa } from '@/types/formulario'
import { oportunidadeService } from '@/services/oportunidadeService'
import { clienteService } from '@/services/clienteService'
import { sacadoService } from '@/services/sacadoService'
import { notaFiscalOperacaoService } from '@/services/notaFiscalOperacaoService'
import {
  Oportunidade,
  PerfilCredito,
  PerfilOperacao,
  DefesaCreditoRequest,
} from '@/types/oportunidade'
import { Cliente } from '@/types/cliente'
import { Sacado } from '@/types/sacado'
import { NotaFiscalOperacao } from '@/types/notaFiscalOperacao'
import { authStore } from '@/store/authStore'

const PERFIL_CREDITO: { value: PerfilCredito; label: string }[] = [
  { value: 'ASSET_BASED', label: 'Asset-Based (foco no sacado)' },
  { value: 'CASHFLOW_BASED', label: 'Cashflow-Based (foco no cedente)' },
  { value: 'HIBRIDO', label: 'Híbrido' },
  { value: 'OUTRO', label: 'Outro' },
]
const PERFIL_OPERACAO: { value: PerfilOperacao; label: string; color: 'success' | 'warning' | 'error' | 'info' }[] = [
  { value: 'ELEGIVEL', label: 'Elegível', color: 'success' },
  { value: 'ELEGIVEL_COM_RESTRICOES', label: 'Elegível com restrições', color: 'warning' },
  { value: 'NAO_ELEGIVEL', label: 'Não elegível', color: 'error' },
  { value: 'EM_ANALISE', label: 'Em análise', color: 'info' },
]

const formatBRL = (v?: number | null) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

const DefesaCreditoPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const canEditar = !!usuario?.permissoes?.some((p) => {
    const up = p.toUpperCase()
    return up === 'ADMIN' || up === 'GERENTE' || up === 'OPERADOR'
  })

  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [gerandoRascunho, setGerandoRascunho] = useState(false)
  /**
   * Documentos que a IA leu no último rascunho (15/10/2026). `null` = nenhum rascunho gerado
   * nesta visita; lista vazia = a IA não tinha leitura de nenhum documento da reserva.
   */
  const [fontesRascunho, setFontesRascunho] = useState<{ nome: string; id: string }[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const [oportunidade, setOportunidade] = useState<Oportunidade | null>(null)
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [sacados, setSacados] = useState<Sacado[]>([])
  const [sacadoSelecionado, setSacadoSelecionado] = useState<Sacado | null>(null)
  const [notas, setNotas] = useState<NotaFiscalOperacao[]>([])

  // ─── Form state — defesa ───────────────────────────────────────────────
  const [dataVencimento, setDataVencimento] = useState('')
  const [perfilCredito, setPerfilCredito] = useState<PerfilCredito | ''>('')
  const [analise, setAnalise] = useState('')
  const [tese, setTese] = useState('')
  const [avaliacao, setAvaliacao] = useState('')
  const [recomendacao, setRecomendacao] = useState('')
  const [perfilOp, setPerfilOp] = useState<PerfilOperacao | ''>('')

  const [pontosPositivos, setPontosPositivos] = useState<string[]>([])
  const [pontosAtencao, setPontosAtencao] = useState<string[]>([])
  const [ppInput, setPpInput] = useState('')
  const [paInput, setPaInput] = useState('')

  // Dialog adicionar NF
  const [nfDialogOpen, setNfDialogOpen] = useState(false)
  const [nfNumero, setNfNumero] = useState('')
  const [nfValor, setNfValor] = useState('')
  const [nfNatureza, setNfNatureza] = useState('')
  const [nfVencimento, setNfVencimento] = useState('')
  const [nfEmissao, setNfEmissao] = useState('')

  const totalNfs = useMemo(
    () => notas.reduce((acc, n) => acc + Number(n.valor ?? 0), 0),
    [notas]
  )

  const carregar = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setErro(null)
    try {
      const [op, sacadosPage, nfs] = await Promise.all([
        oportunidadeService.buscar(id),
        sacadoService.listar({ size: 500 }),
        notaFiscalOperacaoService.listar(id),
      ])
      setOportunidade(op)
      setSacados(sacadosPage.content ?? [])
      setNotas(nfs ?? [])

      // carrega cliente
      try {
        const cli = await clienteService.buscar(op.clienteId)
        setCliente(cli)
      } catch {
        /* ignore */
      }

      // hidrata form
      setDataVencimento(op.dataVencimento ?? '')
      setPerfilCredito((op.perfilCredito as PerfilCredito) ?? '')
      setAnalise(op.analiseFinanceira ?? '')
      setTese(op.teseCreditoFundamento ?? '')
      setAvaliacao(op.avaliacaoCedente ?? '')
      setRecomendacao(op.recomendacao ?? '')
      setPerfilOp((op.perfilOperacao as PerfilOperacao) ?? '')
      setPontosPositivos(op.pontosPositivos ?? [])
      setPontosAtencao(op.pontosAtencao ?? [])

      if (op.sacadoId) {
        const match = (sacadosPage.content ?? []).find((s) => s.id === op.sacadoId)
        setSacadoSelecionado(match ?? null)
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar reserva')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    carregar()
  }, [carregar])

  const addPontoPositivo = () => {
    const v = ppInput.trim()
    if (!v) return
    setPontosPositivos((prev) => [...prev, v])
    setPpInput('')
  }
  const addPontoAtencao = () => {
    const v = paInput.trim()
    if (!v) return
    setPontosAtencao((prev) => [...prev, v])
    setPaInput('')
  }

  /**
   * Rascunho por IA. Duas regras de respeito ao trabalho humano:
   * (1) preenche APENAS os campos vazios — texto que o analista já escreveu não é tocado;
   * (2) nada é salvo: o rascunho entra nos campos e o analista decide.
   * O agente, por sua vez, só cita números do contexto — lacunas viram pontos de atenção.
   */
  const handleGerarRascunho = async () => {
    if (!oportunidade) return
    setGerandoRascunho(true)
    try {
      // A IA lê os documentos da reserva (entrega E, 15/10/2026): a tela manda a reserva e os
      // ids dos arquivos dela — o backend recorta pelo tenant e usa o que já leu de cada um.
      // Sem a lista, o rascunho ainda sai pelos documentos gravados com a reserva.
      // Dados do formulário do produto (L6): só os campos marcados "Enviar à IA" das versões
      // vinculadas, NUNCA CPF — o recorte é do servidor (`/formulario/contexto-ia`); a tela
      // só junta o texto. Falha aqui não impede o rascunho.
      const [documentos, contextoFormulario] = await Promise.all([
        documentoService
          .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, oportunidade.id, 0, 500)
          .then((p) => p.content ?? [])
          .catch(() => [] as Documento[]),
        formularioService.contextoIa(oportunidade.id).catch(() => [] as ItemContextoIa[]),
      ])
      const textoFormulario = contextoFormulario
        .filter((c) => c?.rotulo && c.valor != null && String(c.valor).trim())
        .map((c) => `${c.rotulo}: ${String(c.valor).trim()}`)
        .join('\n')
      const rascunho = await iaService.gerarRascunhoDefesa({
        numeroReserva: oportunidade.numero ?? null,
        razaoSocial: cliente?.razaoSocial ?? oportunidade.razaoSocial ?? null,
        cnpj: cliente?.cnpj ?? oportunidade.cnpj ?? null,
        segmento: cliente?.segmento ?? null,
        produto: oportunidade.tipoNecessidade ?? null,
        valorOperacao: oportunidade.valorEstimado ?? null,
        // Os dados do formulário do produto (cadastro/Estruturação) complementam a
        // justificativa do consultor.
        descricaoOperacao:
          [oportunidade.descricao, textoFormulario]
            .filter((t): t is string => !!t && !!t.trim())
            .join('\n\n') || null,
        posicionamentoEstrategico: oportunidade.posicionamentoEstrategico ?? null,
        presencaGeografica: oportunidade.presencaGeografica ?? null,
        receitaLiquidaAnterior: oportunidade.receitaLiquidaAnterior ?? null,
        receitaLiquidaVigente: oportunidade.receitaLiquidaVigente ?? null,
        resultadoOperacionalAnterior: oportunidade.resultadoOperacionalAnterior ?? null,
        resultadoOperacionalVigente: oportunidade.resultadoOperacionalVigente ?? null,
        patrimonioLiquido: oportunidade.patrimonioLiquido ?? null,
        endividamento: oportunidade.endividamento ?? null,
        caixaAtual: oportunidade.caixaAtual ?? null,
        contasReceber: oportunidade.contasReceber ?? null,
        sacadoNome: sacadoSelecionado?.razaoSocial ?? null,
        reservaId: oportunidade.id,
        documentosIds: documentos.map((d) => d.id),
        // Análise da Originei (entrega H): campo PRÓPRIO do contexto da IA — não entra na
        // descricaoOperacao, que é a justificativa do consultor.
        analiseOriginei: oportunidade.analiseOriginei?.trim() || null,
      })
      const nomePorId = new Map(documentos.map((d) => [d.id, d.nome]))
      setFontesRascunho(
        (rascunho.documentosConsultados ?? []).map((f: FonteDefesa) => ({
          id: f.documentoId,
          nome: nomePorId.get(f.documentoId) ?? f.tipo ?? 'Documento',
        }))
      )

      const preservados: string[] = []
      const aplicar = (
        atual: string,
        novo: string | null | undefined,
        rotulo: string,
        set: (v: string) => void
      ) => {
        if (atual.trim()) {
          preservados.push(rotulo)
        } else if (novo && novo.trim()) {
          set(novo.trim())
        }
      }
      aplicar(analise, rascunho.analiseFinanceira, 'Análise financeira', setAnalise)
      aplicar(tese, rascunho.teseCreditoFundamento, 'Tese de crédito', setTese)
      aplicar(avaliacao, rascunho.avaliacaoCedente, 'Avaliação do cedente', setAvaliacao)
      aplicar(recomendacao, rascunho.recomendacao, 'Recomendação', setRecomendacao)
      if (pontosPositivos.length === 0 && rascunho.pontosPositivos?.length) {
        setPontosPositivos(rascunho.pontosPositivos)
      } else if (pontosPositivos.length > 0) {
        preservados.push('Pontos positivos')
      }
      if (pontosAtencao.length === 0 && rascunho.pontosAtencao?.length) {
        setPontosAtencao(rascunho.pontosAtencao)
      } else if (pontosAtencao.length > 0) {
        preservados.push('Pontos de atenção')
      }

      enqueueSnackbar(
        preservados.length > 0
          ? `Rascunho aplicado nos campos vazios. Mantidos como estavam: ${preservados.join(', ')}.`
          : 'Rascunho aplicado. Revise cada seção antes de salvar — a IA só redige, quem assina é você.',
        { variant: 'success' }
      )
    } catch {
      enqueueSnackbar('Não foi possível gerar o rascunho. Tente novamente.', {
        variant: 'error',
      })
    } finally {
      setGerandoRascunho(false)
    }
  }

  const handleSalvar = async () => {
    if (!id || !oportunidade) return
    setSalvando(true)
    try {
      const payload: DefesaCreditoRequest = {
        sacadoId: sacadoSelecionado?.id ?? null,
        dataVencimento: dataVencimento || null,
        perfilCredito: (perfilCredito || null) as PerfilCredito | null,
        analiseFinanceira: analise.trim() || null,
        teseCreditoFundamento: tese.trim() || null,
        avaliacaoCedente: avaliacao.trim() || null,
        recomendacao: recomendacao.trim() || null,
        perfilOperacao: (perfilOp || null) as PerfilOperacao | null,
        pontosPositivos,
        pontosAtencao,
      }
      await oportunidadeService.atualizarDefesaCredito(id, payload)
      enqueueSnackbar('Defesa de crédito salva', { variant: 'success' })
      await carregar()
    } catch (e: unknown) {
      const msg =
        (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : null) ?? 'Falha ao salvar defesa'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const handleAdicionarNf = async () => {
    if (!id) return
    try {
      await notaFiscalOperacaoService.adicionar(id, {
        numero: nfNumero.trim(),
        valor: Number(nfValor),
        naturezaServico: nfNatureza.trim() || null,
        dataEmissao: nfEmissao || null,
        dataVencimento: nfVencimento,
        sacadoId: sacadoSelecionado?.id ?? null,
      })
      enqueueSnackbar('NF adicionada', { variant: 'success' })
      setNfDialogOpen(false)
      setNfNumero('')
      setNfValor('')
      setNfNatureza('')
      setNfEmissao('')
      setNfVencimento('')
      const nfs = await notaFiscalOperacaoService.listar(id)
      setNotas(nfs)
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao adicionar NF', {
        variant: 'error',
      })
    }
  }

  const handleRemoverNf = async (nfId: string) => {
    if (!id) return
    if (!window.confirm('Remover esta NF?')) return
    try {
      await notaFiscalOperacaoService.deletar(id, nfId)
      setNotas((prev) => prev.filter((n) => n.id !== nfId))
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao remover NF', {
        variant: 'error',
      })
    }
  }

  const handleDownloadPdf = async () => {
    if (!id) return
    try {
      const cli = cliente as (Cliente & { dataConstituicao?: string; atividadePrincipal?: string }) | null
      const enderecoTxt = cli?.endereco
        ? [cli.endereco.rua, cli.endereco.numero, cli.endereco.cidade, cli.endereco.estado]
            .filter(Boolean)
            .join(', ')
        : null
      const blob = await oportunidadeService.gerarPdfDefesaCredito(id, {
        cedente: cli
          ? {
              razaoSocial: cli.razaoSocial,
              cnpj: cli.cnpj,
              dataConstituicao: cli.dataConstituicao ?? null,
              atividadePrincipal: cli.atividadePrincipal ?? null,
              endereco: enderecoTxt,
            }
          : undefined,
        sacado: sacadoSelecionado
          ? { razaoSocial: sacadoSelecionado.razaoSocial, cnpj: sacadoSelecionado.cnpj }
          : undefined,
      })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao gerar PDF', {
        variant: 'error',
      })
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (erro || !oportunidade) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro ?? 'Reserva não encontrada'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/estruturacao')}>
          Voltar para Estruturação
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/estruturacao')}
          sx={{ cursor: 'pointer' }}
        >
          Estruturação
        </Link>
        <Typography color="textPrimary">Defesa de Crédito</Typography>
      </Breadcrumbs>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/estruturacao')}>
          Voltar
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            Defesa de Crédito
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cliente?.razaoSocial ?? 'Cedente'} · {oportunidade.tipoNecessidade} ·{' '}
            {formatBRL(oportunidade.valorEstimado)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPdf}
          color="primary"
        >
          Gerar PDF
        </Button>
        {canEditar && (
          <Button
            variant="outlined"
            startIcon={
              gerandoRascunho ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />
            }
            onClick={handleGerarRascunho}
            disabled={gerandoRascunho || salvando}
          >
            {gerandoRascunho ? 'Gerando rascunho…' : 'Gerar rascunho com IA'}
          </Button>
        )}
        {canEditar && (
          <Button
            variant="contained"
            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        )}
      </Stack>

      {/* De onde saiu o rascunho (15/10/2026): os documentos da reserva que a IA leu. */}
      {fontesRascunho &&
        (fontesRascunho.length > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }} aria-label="Documentos lidos pela IA">
            Rascunho redigido a partir de {fontesRascunho.length} documento
            {fontesRascunho.length > 1 ? 's' : ''} da reserva: {fontesRascunho.map((f) => f.nome).join(', ')}.
            Os campos preenchidos entraram como complemento — confira cada número antes de salvar.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }} aria-label="Documentos lidos pela IA">
            Nenhum documento da reserva tinha leitura da IA — o rascunho usou só os campos preenchidos.
            Documentos enviados há pouco podem levar alguns minutos para serem lidos; gere de novo depois.
          </Alert>
        ))}

      <Grid container spacing={3}>
        {/* ── Visão geral ─────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="1. Visão geral da operação" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Valor da operação"
                    value={formatBRL(oportunidade.valorEstimado)}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Produto"
                    value={oportunidade.tipoNecessidade}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Vencimento"
                    type="date"
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={!canEditar}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={!canEditar}>
                    <InputLabel>Perfil de crédito</InputLabel>
                    <Select
                      value={perfilCredito}
                      label="Perfil de crédito"
                      onChange={(e) => setPerfilCredito(e.target.value as PerfilCredito | '')}
                    >
                      <MenuItem value="">—</MenuItem>
                      {PERFIL_CREDITO.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Cedente ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="2. Cedente"
              subheader="Dados do tomador (cliente)"
              action={
                cliente && (
                  <Button
                    size="small"
                    onClick={() => navigate(`/clientes/${cliente.id}/editar`)}
                  >
                    Editar cadastro
                  </Button>
                )
              }
            />
            <CardContent>
              {!cliente ? (
                <Alert severity="info">Cliente não carregado.</Alert>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Razão Social:</strong> {cliente.razaoSocial}
                  </Typography>
                  <Typography variant="body2">
                    <strong>CNPJ:</strong> {cliente.cnpj}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Setor:</strong> {cliente.setorIndustria}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Data de constituição:</strong>{' '}
                    {(cliente as unknown as { dataConstituicao?: string }).dataConstituicao ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Atividade principal:</strong>{' '}
                    {(cliente as unknown as { atividadePrincipal?: string }).atividadePrincipal ?? '—'}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Sacado ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Sacado" subheader="Devedor principal das NFs" />
            <CardContent>
              <Autocomplete
                options={sacados}
                value={sacadoSelecionado}
                onChange={(_, v) => setSacadoSelecionado(v)}
                getOptionLabel={(opt) => `${opt.razaoSocial} — CNPJ ${opt.cnpj}`}
                disabled={!canEditar}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sacado"
                    placeholder="Selecione o sacado principal"
                    helperText="Use o menu Sacados para cadastrar um novo"
                  />
                )}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ── Análise da Originei (entrega H) — interna: só o back-office vê; o backend
             já devolve nulo para o consultor. Também vai como contexto para a IA. ── */}
        {canEditar && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="3. Análise da Originei"
                subheader="Redigida na Estruturação · uso interno"
              />
              <CardContent>
                <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
                  <Typography
                    variant="body2"
                    whiteSpace="pre-wrap"
                    color={oportunidade.analiseOriginei?.trim() ? 'text.primary' : 'text.secondary'}
                  >
                    {oportunidade.analiseOriginei?.trim()
                      ? oportunidade.analiseOriginei
                      : 'Ainda não redigida — escreva em Estruturação › Análise.'}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* ── Posicionamento e presença (análise estruturada antiga) — só se existir ── */}
        {(oportunidade.posicionamentoEstrategico || oportunidade.presencaGeografica) && (
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                canEditar
                  ? 'Análise anterior — posicionamento estratégico e presença geográfica'
                  : '3. Posicionamento estratégico e presença geográfica'
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Posicionamento estratégico
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
                    <Typography variant="body2" whiteSpace="pre-wrap">
                      {oportunidade.posicionamentoEstrategico ?? '—'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Presença geográfica
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
                    <Typography variant="body2" whiteSpace="pre-wrap">
                      {oportunidade.presencaGeografica ?? '—'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* ── NFs ─────────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="4. Detalhamento dos recebíveis"
              subheader={`Total: ${formatBRL(totalNfs)} · ${notas.length} NF${notas.length !== 1 ? 's' : ''}`}
              action={
                canEditar && (
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNfDialogOpen(true)}>
                    Adicionar NF
                  </Button>
                )
              }
            />
            <CardContent>
              {notas.length === 0 ? (
                <Alert severity="info">Nenhuma NF vinculada à operação.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>NF</strong></TableCell>
                        <TableCell><strong>VALOR</strong></TableCell>
                        <TableCell><strong>NATUREZA DO SERVIÇO</strong></TableCell>
                        <TableCell><strong>EMISSÃO</strong></TableCell>
                        <TableCell><strong>VENCIMENTO</strong></TableCell>
                        {canEditar && <TableCell align="right"><strong>AÇÕES</strong></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notas.map((nf) => (
                        <TableRow key={nf.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{nf.numero}</TableCell>
                          <TableCell>{formatBRL(nf.valor)}</TableCell>
                          <TableCell>{nf.naturezaServico ?? '—'}</TableCell>
                          <TableCell>
                            {nf.dataEmissao
                              ? new Date(nf.dataEmissao).toLocaleDateString('pt-BR')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {new Date(nf.dataVencimento).toLocaleDateString('pt-BR')}
                          </TableCell>
                          {canEditar && (
                            <TableCell align="right">
                              <Tooltip title="Remover">
                                <IconButton size="small" onClick={() => handleRemoverNf(nf.id)}>
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Análise financeira ─────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="5. Análise financeira"
              subheader="Indicadores vêm da Estruturação — comentário aberto abaixo"
            />
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Receita líquida anterior
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.receitaLiquidaAnterior)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Receita líquida vigente
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.receitaLiquidaVigente)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Resultado op. anterior
                    </Typography>
                    <Typography
                      fontWeight={700}
                      color={
                        (oportunidade.resultadoOperacionalAnterior ?? 0) < 0 ? 'error' : 'inherit'
                      }
                    >
                      {formatBRL(oportunidade.resultadoOperacionalAnterior)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Resultado op. vigente
                    </Typography>
                    <Typography
                      fontWeight={700}
                      color={
                        (oportunidade.resultadoOperacionalVigente ?? 0) < 0 ? 'error' : 'inherit'
                      }
                    >
                      {formatBRL(oportunidade.resultadoOperacionalVigente)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Patrimônio líquido
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.patrimonioLiquido)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Endividamento
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.endividamento)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Caixa atual
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.caixaAtual)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Contas a receber
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatBRL(oportunidade.contasReceber)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              <TextField
                label="Leitura / interpretação da análise financeira"
                value={analise}
                onChange={(e) => setAnalise(e.target.value)}
                placeholder="Comentário consolidado sobre receita, resultado, estrutura de capital e liquidez"
                multiline
                rows={5}
                fullWidth
                disabled={!canEditar}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ── Análise de risco ───────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="6a. Pontos positivos" />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Adicione um ponto positivo"
                  value={ppInput}
                  onChange={(e) => setPpInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPontoPositivo())}
                  fullWidth
                  disabled={!canEditar}
                />
                <Button variant="outlined" onClick={addPontoPositivo} disabled={!canEditar}>
                  <AddIcon />
                </Button>
              </Stack>
              <Stack spacing={0.5} flexWrap="wrap" useFlexGap direction="row">
                {pontosPositivos.map((p, i) => (
                  <Chip
                    key={`${p}-${i}`}
                    label={p}
                    color="success"
                    variant="outlined"
                    onDelete={
                      canEditar
                        ? () => setPontosPositivos((prev) => prev.filter((_, idx) => idx !== i))
                        : undefined
                    }
                    sx={{ mb: 0.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="6b. Pontos de atenção" />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Adicione um ponto de atenção"
                  value={paInput}
                  onChange={(e) => setPaInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPontoAtencao())}
                  fullWidth
                  disabled={!canEditar}
                />
                <Button variant="outlined" onClick={addPontoAtencao} disabled={!canEditar}>
                  <AddIcon />
                </Button>
              </Stack>
              <Stack spacing={0.5} flexWrap="wrap" useFlexGap direction="row">
                {pontosAtencao.map((p, i) => (
                  <Chip
                    key={`${p}-${i}`}
                    label={p}
                    color="warning"
                    variant="outlined"
                    onDelete={
                      canEditar
                        ? () => setPontosAtencao((prev) => prev.filter((_, idx) => idx !== i))
                        : undefined
                    }
                    sx={{ mb: 0.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Tese de crédito + avaliação do cedente ─────────────────── */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="7. Tese de crédito e avaliação do cedente" />
            <CardContent>
              <TextField
                label="Fundamentação da tese (foco primário do risco, lastro etc.)"
                value={tese}
                onChange={(e) => setTese(e.target.value)}
                multiline
                rows={5}
                fullWidth
                sx={{ mb: 2 }}
                disabled={!canEditar}
              />
              <TextField
                label="Avaliação do cedente"
                value={avaliacao}
                onChange={(e) => setAvaliacao(e.target.value)}
                placeholder="Produto validado, clientes relevantes, potencial de escala, etc."
                multiline
                rows={4}
                fullWidth
                disabled={!canEditar}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ── Conclusão ─────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="8. Conclusão e recomendação" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!canEditar}>
                    <InputLabel>Perfil da operação</InputLabel>
                    <Select
                      value={perfilOp}
                      label="Perfil da operação"
                      onChange={(e) => setPerfilOp(e.target.value as PerfilOperacao | '')}
                    >
                      <MenuItem value="">—</MenuItem>
                      {PERFIL_OPERACAO.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          <Chip label={p.label} size="small" color={p.color} sx={{ mr: 1 }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Valor da operação (confirmação)"
                    value={formatBRL(oportunidade.valorEstimado)}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Recomendação Originei"
                    value={recomendacao}
                    onChange={(e) => setRecomendacao(e.target.value)}
                    placeholder="Texto final da recomendação — sustentação da elegibilidade e condições sugeridas"
                    multiline
                    rows={5}
                    fullWidth
                    disabled={!canEditar}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Dialog NF ─────────────────────────────────────────────── */}
      {nfDialogOpen && (
        <Paper
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.45)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper sx={{ p: 3, width: '100%', maxWidth: 520 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Adicionar NF à operação
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Número da NF"
                value={nfNumero}
                onChange={(e) => setNfNumero(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Valor"
                type="number"
                value={nfValor}
                onChange={(e) => setNfValor(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                required
                fullWidth
              />
              <TextField
                label="Natureza do serviço"
                value={nfNatureza}
                onChange={(e) => setNfNatureza(e.target.value)}
                placeholder="Ex.: Licenciamento de software, Implementação…"
                fullWidth
                multiline
                rows={2}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Data de emissão"
                  type="date"
                  value={nfEmissao}
                  onChange={(e) => setNfEmissao(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Data de vencimento"
                  type="date"
                  value={nfVencimento}
                  onChange={(e) => setNfVencimento(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={() => setNfDialogOpen(false)}>Cancelar</Button>
              <Button
                variant="contained"
                onClick={handleAdicionarNf}
                disabled={!nfNumero || !nfValor || !nfVencimento}
              >
                Adicionar
              </Button>
            </Stack>
          </Paper>
        </Paper>
      )}
    </Box>
  )
}

export default DefesaCreditoPage
